import {
  CheckIcon,
  EllipsisIcon,
  HandIcon,
  LockOpenIcon,
  PencilIcon,
  RefreshCwIcon,
  SendIcon,
  Undo2Icon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

import {
  OrderAction,
  OrderStatus,
  type OrderDTO,
  type UpdateOrderDTO,
} from '@invenco/sales-interface/interfaces';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { ButtonSkeleton } from '@/components/button-skeleton';
import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { FormDialog } from '@/components/forms/form-dialog';
import { HeaderActions } from '@/components/header';
import { Result } from '@/shared/helpers/Result';

import { orderActionToManagedAction } from './order-action-details';
import { OrderEditDialogContent } from './order-edit-dialog-content';

type OrderHeaderAction = {
  id: 'edit' | OrderAction;
  label: string;
  icon: LucideIcon;
  /** Display order: higher priority renders further right, closest to the primary position. */
  priority: number;
  menuOnly?: boolean;
  primary?: boolean;
  confirmation?: {
    title: string;
    description: string;
  };
  onSelect?: () => Promise<Result>;
};

type Props = {
  order?: OrderDTO;
  isLoading?: boolean;
  onRefresh: () => Promise<unknown>;
  updateOrder: (patch: UpdateOrderDTO) => Promise<Result<OrderDTO>>;
  applyManualHold: () => Promise<Result>;
  releaseManualHold: () => Promise<Result>;
  revertToDraft: () => Promise<Result>;
  releaseOrder: () => Promise<Result>;
  cancelOrder: () => Promise<Result>;
  closeOrder: () => Promise<Result>;
};

const LOADING_SKELETONS = 2;
const CONFIRMATION_DESCRIPTION =
  'This action cannot be undone, and the order will not be processed';

const orderActionConfig: Record<OrderAction, Omit<OrderHeaderAction, 'id' | 'onSelect'>> = {
  [OrderAction.APPLY_MANUAL_HOLD]: {
    label: 'Hold',
    icon: HandIcon,
    priority: 80,
  },
  [OrderAction.RELEASE_MANUAL_HOLD]: {
    label: 'Release hold',
    icon: LockOpenIcon,
    priority: 85,
    primary: true,
  },
  [OrderAction.REVERT_TO_DRAFT]: {
    label: 'Revert to draft',
    icon: Undo2Icon,
    priority: 70,
  },
  [OrderAction.RELEASE]: {
    label: 'Release',
    icon: SendIcon,
    priority: 90,
    primary: true,
  },
  [OrderAction.CANCEL]: {
    label: 'Cancel',
    icon: XIcon,
    priority: 50,
    menuOnly: true,
    confirmation: {
      title: 'Are you sure you want to cancel the order?',
      description: CONFIRMATION_DESCRIPTION,
    },
  },
  [OrderAction.CLOSE]: {
    label: 'Close',
    icon: CheckIcon,
    priority: 40,
    menuOnly: true,
    confirmation: {
      title: 'Are you sure you want to close the order?',
      description: CONFIRMATION_DESCRIPTION,
    },
  },
};

export function OrderActions({
  order,
  isLoading,
  onRefresh,
  updateOrder,
  applyManualHold,
  releaseManualHold,
  revertToDraft,
  releaseOrder,
  cancelOrder,
  closeOrder,
}: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [performingAction, setPerformingAction] = useState<OrderHeaderAction['id']>();

  const actionHandlers: Record<OrderAction, () => Promise<Result>> = {
    [OrderAction.APPLY_MANUAL_HOLD]: applyManualHold,
    [OrderAction.RELEASE_MANUAL_HOLD]: releaseManualHold,
    [OrderAction.REVERT_TO_DRAFT]: revertToDraft,
    [OrderAction.RELEASE]: releaseOrder,
    [OrderAction.CANCEL]: cancelOrder,
    [OrderAction.CLOSE]: closeOrder,
  };

  const actions: OrderHeaderAction[] = [];

  const canEdit = order?.status === OrderStatus.DRAFT;

  if (canEdit) {
    actions.push({
      id: 'edit',
      label: 'Edit',
      icon: PencilIcon,
      priority: 30,
    });
  }

  for (const action of order?.availableActions ?? []) {
    const managedAction = orderActionToManagedAction[action];
    if (managedAction && order?.managedActions?.includes(managedAction)) {
      continue;
    }

    actions.push({
      id: action,
      ...orderActionConfig[action],
      onSelect: actionHandlers[action],
    });
  }

  // At most three direct actions can coexist (draft: edit + hold + release), so all of them
  // render as buttons and only menu-only (destructive) actions go in the more-actions menu.
  const directActions = actions
    .filter((action) => !action.menuOnly)
    .sort((a, b) => a.priority - b.priority);
  const menuActions = actions
    .filter((action) => action.menuOnly)
    .sort((a, b) => b.priority - a.priority);
  // Only one action renders as primary: the highest-priority one flagged primary (the rightmost).
  const primaryActionId = directActions.findLast((action) => action.primary)?.id;

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const selectAction = async (action: OrderHeaderAction): Promise<Result> => {
    if (!action.onSelect) return Result.ok();

    setPerformingAction(action.id);
    try {
      return await action.onSelect();
    } finally {
      setPerformingAction(undefined);
    }
  };

  return (
    <HeaderActions>
      {isLoading ? (
        <Button variant="ghost" size="icon" aria-label="More actions" disabled>
          <EllipsisIcon />
        </Button>
      ) : menuActions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {menuActions.map((action) => {
              const Icon = action.icon;

              if (action.confirmation) {
                return (
                  <AlertDialogWithAction key={action.id} onAction={() => selectAction(action)}>
                    <AlertDialogWithActionTrigger asChild>
                      <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                        <Icon />
                        {action.label}
                      </DropdownMenuItem>
                    </AlertDialogWithActionTrigger>
                    <AlertDialogWithActionContent>
                      <AlertDialogWithActionHeader>
                        <AlertDialogWithActionTitle>
                          {action.confirmation.title}
                        </AlertDialogWithActionTitle>
                        <AlertDialogWithActionDescription>
                          {action.confirmation.description}
                        </AlertDialogWithActionDescription>
                      </AlertDialogWithActionHeader>
                      <AlertDialogWithActionFooter>
                        <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
                        <AlertDialogWithActionSubmit>Confirm</AlertDialogWithActionSubmit>
                      </AlertDialogWithActionFooter>
                    </AlertDialogWithActionContent>
                  </AlertDialogWithAction>
                );
              }

              return (
                <DropdownMenuItem key={action.id} onSelect={() => void selectAction(action)}>
                  <Icon />
                  {action.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button
        size="icon"
        variant="ghost"
        aria-label="Refresh"
        loading={isRefreshing}
        disabled={isLoading}
        onClick={() => void refresh()}
      >
        <RefreshCwIcon />
      </Button>
      {isLoading
        ? Array.from({ length: LOADING_SKELETONS }, (_, index) => (
            <ButtonSkeleton key={index} data-testid="order-action-skeleton" />
          ))
        : directActions.map((action) => {
            const Icon = action.icon;

            if (action.id === 'edit' && order) {
              return (
                <FormDialog key={action.id}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Icon />
                      {action.label}
                    </Button>
                  </DialogTrigger>
                  <OrderEditDialogContent order={order} onSubmit={updateOrder} />
                </FormDialog>
              );
            }

            return (
              <Button
                key={action.id}
                variant={action.id === primaryActionId ? 'default' : 'outline'}
                loading={performingAction === action.id}
                disabled={!!performingAction && performingAction !== action.id}
                onClick={() => void selectAction(action)}
              >
                <Icon />
                {action.label}
              </Button>
            );
          })}
    </HeaderActions>
  );
}
